import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { logout, getCurrentUser } from './api';
import packageJson from '../package.json';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import { UsersPanel } from './Users';
import { ApiKeysPanel } from './ApiKeys';
import { ExtensionTokensPanel } from './ExtensionTokens';
import { ASSOCIATES_REPORTS_URL } from './landing/AmazonPanel';
import DataSourcesPanel from './donext/DataSourcesPanel';

const LOOKER_URL = 'https://lookerstudio.google.com/reporting/e9bf2b49-9f63-407f-b344-274125dfe17d/page/kIV1C';
const ADSENSE_URL = 'https://adsense.google.com/adsense/u/0/pub-4556132808286686/reporting/?savedreport=Earnings%20by%20Site';

const IntegrationLink = ({ to, href, title, description }) => {
  const body = (
    <>
      <div style={{ fontWeight: 500 }}>
        {title}
        {href && <span className="muted small" style={{ marginLeft: '6px' }}>↗</span>}
      </div>
      <div className="muted small">{description}</div>
    </>
  );
  const style = {
    display: 'block',
    padding: '12px 14px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--color-text)',
    textDecoration: 'none',
  };
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" style={style}>{body}</a>
  ) : (
    <Link to={to} style={style}>{body}</Link>
  );
};

const Settings = ({ setAuthenticated }) => {
  const navigate = useNavigate();
  const [userPhoto, setUserPhoto] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserPhoto(user.photoURL);
        setUserName(user.displayName || user.email || '');
        setUserEmail(user.email || '');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const me = await getCurrentUser();
        setIsAdmin(me?.is_admin || false);
        // Firebase may have no session (API-key or legacy login); fall back to /me.
        setUserName(prev => prev || me?.name || me?.email || '');
        setUserEmail(prev => prev || me?.email || '');
      } catch (err) {
        // ignore
      }
    };
    fetchUser();
  }, []);

  /* /settings#data-sources (linked from the home page's freshness line) */
  useEffect(() => {
    if (window.location.hash !== '#data-sources') return;
    const el = document.getElementById('data-sources');
    if (el) el.scrollIntoView({ block: 'start' });
  }, []);

  const handleLogout = () => {
    logout();
    setAuthenticated(false);
    navigate('/login');
  };

  const apiDocsUrl = `${process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://127.0.0.1:8000'}/docs`;

  return (
    <div className="page page-narrow">
      <PageHeader title="Settings" subtitle="Your profile, team access, API keys, data sources and integrations" />

      <section className="card">
        <h2 className="card-title">Profile</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {userPhoto ? (
            <img
              src={userPhoto}
              alt={userName}
              referrerPolicy="no-referrer"
              style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              backgroundColor: 'var(--color-muted)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', fontWeight: 'bold', flexShrink: 0,
            }}>
              {(userName || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontSize: '17px', fontWeight: 600 }}>{userName}</div>
            <div className="muted">{userEmail}</div>
            {isAdmin && <div style={{ marginTop: '4px' }}><Badge state="info">Admin</Badge></div>}
          </div>
        </div>
      </section>

      <section className="card">
        <h2 className="card-title">Team access</h2>
        <UsersPanel />
      </section>

      <section className="card">
        <h2 className="card-title">API keys</h2>
        <ApiKeysPanel />
      </section>

      <section className="card">
        <h2 className="card-title">Save to DAM extension</h2>
        <ExtensionTokensPanel />
      </section>

      <section className="card" id="data-sources">
        <h2 className="card-title">Data sources</h2>
        <DataSourcesPanel />
      </section>

      <section className="card">
        <h2 className="card-title">Integrations</h2>
        <div className="card-grid">
          <IntegrationLink href={apiDocsUrl} title="API documentation" description="OpenAPI reference for the backend" />
          <IntegrationLink to="/mcp" title="MCP / agent reference" description="How agents and CLI tools use the API" />
          <IntegrationLink href={LOOKER_URL} title="Analytics" description="Looker Studio traffic report" />
          <IntegrationLink href={ADSENSE_URL} title="Monetization" description="AdSense earnings by site" />
          <IntegrationLink href={ASSOCIATES_REPORTS_URL} title="Amazon Associates" description="Reports to download and import on the Sites page" />
        </div>
      </section>

      <section className="card">
        <h2 className="card-title">About</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
          <span>Version</span>
          <span className="muted">v{packageJson.version}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--color-border)' }}>
          <span>Build</span>
          <span className="muted">{process.env.REACT_APP_BUILD_DATE || 'dev'}</span>
        </div>
      </section>

      <section className="card">
        <div className="card-header" style={{ marginBottom: 0 }}>
          <div>
            <h2 className="card-title">Session</h2>
            <div className="muted small">Signed in as {userEmail || userName || 'unknown'}</div>
          </div>
          <button type="button" className="btn btn-danger" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </section>
    </div>
  );
};

export default Settings;
